import * as Yup from 'yup';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import LoadingButton from '@mui/lab/LoadingButton';
import { alpha, useTheme } from '@mui/material/styles';
import InputAdornment from '@mui/material/InputAdornment';

import axios, { endpoints } from 'src/utils/axios';

import { useAuthContext } from 'src/auth/hooks';

import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import FormProvider, { RHFTextField } from 'src/components/hook-form';

// ----------------------------------------------------------------------

const ChangePasswordSchema = Yup.object().shape({
  oldPassword: Yup.string()
    .min(3, 'La contraseña actual debe tener al menos 6 caracteres')
    .required('La contraseña actual es requerida'),
  newPassword: Yup.string()
    .min(6, 'La nueva contraseña debe tener al menos 6 caracteres')
    .required('La nueva contraseña es requerida'),
  confirmPassword: Yup.string()
    .required('Confirmar contraseña es requerido')
    .oneOf([Yup.ref('newPassword')], 'Las contraseñas deben coincidir'),
});

const defaultValues = {
  oldPassword: '',
  newPassword: '',
  confirmPassword: '',
};

export default function BlockingPasswordChangeModal() {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuthContext();

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(ChangePasswordSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = methods;

  const handleChangePassword = handleSubmit(async (data) => {
    try {
      const response = await axios.post(endpoints.auth.changePassword, {
        oldPassword: data.oldPassword,
        newPassword: data.newPassword,
        confirmPassword: data.confirmPassword,
      });

      if (response.data) {
        enqueueSnackbar('Contraseña actualizada exitosamente', { variant: 'success' });
        reset();
        window.location.reload();
      }
    } catch (error) {
      console.error('Error changing password:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error al cambiar la contraseña', {
        variant: 'error',
      });
    }
  });

  const handleToggleOldPassword = () => setShowOldPassword(!showOldPassword);
  const handleToggleNewPassword = () => setShowNewPassword(!showNewPassword);
  const handleToggleConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

  if (!user?.needChangePassword) {
    return null;
  }

  return (
    <Dialog
      open
      disableEscapeKeyDown
      disableBackdropClick
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: theme.shadows[24],
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: alpha(theme.palette.grey[900], 0.8),
            backdropFilter: 'blur(8px)',
          },
        },
      }}
    >
      <FormProvider methods={methods} onSubmit={handleChangePassword}>
        <Box sx={{ p: 4 }}>
          <Stack spacing={3} alignItems="center">
            <Stack spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <Iconify icon="solar:lock-password-bold" width={40} sx={{ color: 'white' }} />
              </Box>

              <Typography variant="h4" textAlign="center">
                Cambio de Contraseña Requerido
              </Typography>

              <Typography
                variant="body2"
                textAlign="center"
                sx={{ color: 'text.secondary', maxWidth: 400 }}
              >
                Por seguridad, debes cambiar tu contraseña antes de continuar usando la aplicación.
              </Typography>
            </Stack>

            <Stack spacing={3} sx={{ width: '100%' }}>
              <RHFTextField
                name="oldPassword"
                label="Contraseña Actual"
                type={showOldPassword ? 'text' : 'password'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleToggleOldPassword} edge="end">
                        <Iconify
                          icon={showOldPassword ? 'solar:eye-bold' : 'solar:eye-closed-bold'}
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <RHFTextField
                name="newPassword"
                label="Nueva Contraseña"
                sx={{ width: '100%' }}
                type={showNewPassword ? 'text' : 'password'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleToggleNewPassword} edge="end">
                        <Iconify
                          icon={showNewPassword ? 'solar:eye-bold' : 'solar:eye-closed-bold'}
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <RHFTextField
                name="confirmPassword"
                label="Confirmar Nueva Contraseña"
                type={showConfirmPassword ? 'text' : 'password'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleToggleConfirmPassword} edge="end">
                        <Iconify
                          icon={showConfirmPassword ? 'solar:eye-bold' : 'solar:eye-closed-bold'}
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <LoadingButton
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                loading={isSubmitting}
                sx={{ mt: 2 }}
              >
                Actualizar Contraseña
              </LoadingButton>
            </Stack>

            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.warning.main, 0.1),
                border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Iconify
                  icon="solar:shield-warning-bold"
                  width={20}
                  sx={{ color: 'warning.main', mt: 0.2 }}
                />
                <Typography variant="caption" sx={{ color: theme.palette.mode === 'dark' ? 'warning.main' : 'warning.darker' }}>
                  Esta acción es obligatoria por motivos de seguridad. No podrás acceder a la
                  aplicación hasta que cambies tu contraseña.
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </Box>
      </FormProvider>
    </Dialog>
  );
}
